import React from "react";
import {
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Image,
  Show,
  Text,
  VStack,
  keyframes,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

// Chakra UI motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionButton = motion(Button);

// Floating animation for image
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// Bounce animation for scroll indicator
const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(8px); }
`;

function Hero() {
  return (
    <Box
      position="relative"
      overflow="hidden"
      bg="white"
    >
      <Container
        maxW="7xl"
        pt={{ base: "0", md: "0" }}
        pb={{ base: "2rem", md: "3rem" }}
        px={{ base: 4, md: 8 }}
        position="relative"
        zIndex={1}
      >
        <HStack
          justify="space-between"
          align="center"
          spacing={{ base: "2rem", xl: "4rem" }}
          minH={{ base: "auto", xl: "550px" }}
        >
          <VStack
            flex="1"
            align="start"
            textAlign="start"
            spacing="2rem"
            maxW={{ base: "100%", xl: "550px" }}
            minW={{ xl: "450px" }}
          >
            <MotionHeading
              as="h1"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              lineHeight="1.1"
              fontWeight="700"
              letterSpacing="-0.02em"
              color="gray.900"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.6, 0.05, 0.01, 0.9] }}
            >
              Craft Your{" "}
              <Box as="span" color="orange.500">
                Resume
              </Box>{" "}
              Today
            </MotionHeading>

            <MotionText
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              lineHeight="1.7"
              maxW="500px"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.6, 0.05, 0.01, 0.9] }}
            >
              Tailor your resume with our subject matter expertise across ML/AI Engineering, Data Science, DevOps, Cloud Architecture, and emerging tech roles.
            </MotionText>

            <MotionButton
              as={NavLink}
              to="/login"
              size="lg"
              h="56px"
              px="2.5rem"
              fontSize="md"
              fontWeight="600"
              colorScheme="blue"
              bg="blue.500"
              color="white"
              borderRadius="lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.6, 0.05, 0.01, 0.9] }}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.2 },
              }}
              whileTap={{ scale: 0.98 }}
              _hover={{
                bg: "blue.600",
              }}
              _focus={{ boxShadow: "none" }}
            >
              Get Started
            </MotionButton>

            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Text fontSize="sm" color="gray.500" fontWeight="500">
                Trusted by 10,000+ professionals
              </Text>
            </MotionBox>
          </VStack>

          <Show above="xl">
            <MotionBox
              flexShrink={0}
              initial={{ opacity: 0, x: 30 }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{ duration: 1, delay: 0.3, ease: [0.6, 0.05, 0.01, 0.9] }}
              animation={`${float} 6s ease-in-out infinite`}
            >
              <Image
                src="assets/images/hero-image.png"
                alt="SkillBack Resume Builder Dashboard - LLM-enhanced resume creation interface"
                width="750px"
                height="600px"
                objectFit="contain"
                fallbackSrc="https://via.placeholder.com/750x600"
              />
            </MotionBox>
          </Show>
        </HStack>

        {/* Scroll Indicator */}
        <MotionBox
          position="absolute"
          bottom="2rem"
          left="50%"
          transform="translateX(-50%)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <VStack spacing={2}>
            <Text fontSize="xs" color="gray.500" fontWeight="500" letterSpacing="wider">
              SCROLL TO EXPLORE
            </Text>
            <Box
              animation={`${bounce} 2s ease-in-out infinite`}
              cursor="pointer"
              onClick={() => {
                const workflowSection = document.getElementById('workflow-section');
                if (workflowSection) {
                  workflowSection.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: "#9CA3AF" }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default Hero;
